<template>
  <div class="container">
    <div class="row">
      <div class="col-md-8 col-md-offset-2">
        <div class="bsd-section">
          <breadcrumb />
        </div>

        <pix-loader v-if="loading" />

        <pix-generator
          v-else
          from="billPayment"
          :title="`Mensalidade de ${billTitle}`"
          :title-on-ready="`Copie o QR Code para pagar a mensalidade de ${bill.month_name}`"
          :payment="billPayment"
          :ready="pixReady"
          :waiting="pixWaiting"
          :unavailable="pixUnavailable"
          :callback-clipboard="copySuccess"
          :handle-generate-pix="createBillPayment"
          :has-error="error"
        >
          <price-card :full-value="parseFloat(bill.full_value)" :due-date="bill.due_date" />
        </pix-generator>

        <div class="bsd-section">
          <alert-box ref="alertBox" class="spacer-l" />

          <template v-if="error">
            <router-link
              tag="button"
              :to="{ name: 'panel'}"
              class="z-button z-button--secondary z-button--block"
            >
              Voltar para página inicial
            </router-link>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import AlertBox from 'components/zilla/alert-box';
import Breadcrumb from 'components/breadcrumb';
import DashboardService from 'services/DashboardService';
import PixService from 'services/PixService';
import PriceCard from 'components/price-card';
import PixLoader from 'content-loaders/pix/pix-loader';
import PixGenerator from 'components/pix/pix-generator';

export default {
  components: {
    AlertBox,
    PixLoader,
    PixGenerator,
    Breadcrumb,
    PriceCard,
  },
  data() {
    return {
      bill: {},
      billPayment: {},
      error: false,
      loading: true,
      pixUnavailable: false,
      pixWaiting: false,
      pixReady: false,
      poolingAttempts: 0,
    };
  },
  computed: {
    ...mapState([
      'currentBilling',
    ]),
    billingId() {
      return this.currentBilling.id;
    },
    billId() {
      return this.$route.params.billId;
    },
    reenrollmentBill() {
      return this.bill.metadata?.reenrollment_id;
    },
    billTitle() {
      if (this.reenrollmentBill) return `${this.bill.month_name} (Rematrícula)`;

      return this.bill.month_name;
    },
  },
  methods: {
    loadBill() {
      return DashboardService.getBill(this.billId).then((bill) => {
        this.bill = bill;
        this.$tracker.logEvent('Viewed Pix Page', { billId: this.bill.id });
      });
    },
    loadBillPayment(firstAttempt = false) {
      PixService.getPixBillPayment(this.billingId, this.billId).then((data) => {
        this.billPayment = data;
        this.validateBillPayment(firstAttempt);
        this.loading = false;
      }).catch((error) => {
        this.showErrorMessage();
        this.$rollbar.error(error);
      });
    },
    createBillPayment() {
      this.pixWaiting = true;
      this.pixUnavailable = false;

      this.setLoadingTimeout();
      PixService.createPixBillPayment(this.billingId, this.billId).then((data) => {
        this.billPayment = data;
        this.validateBillPayment();
      }).catch((error) => {
        this.showErrorMessage();
        this.$rollbar.error(error);
      }).finally(this.hideAlert);
    },
    poolingPayment() {
      this.setLoadingTimeout();

      if (this.poolingAttempts >= 10) {
        this.poolingAttempts = 0;
        this.showErrorMessage();

        return;
      }

      setTimeout(async () => {
        this.poolingAttempts += 1;
        this.loadBillPayment();
      }, 2500);
    },
    validateBillPayment(firstAttempt) {
      this.resetPixStatus();

      if (this.bill && this.bill.status === 'paid') {
        this.$router.push({ name: 'payment-receipt', params: { billId: this.bill.id } });
      } else if (this.billPayment === null) {
        if (firstAttempt) {
          this.pixUnavailable = true;
        } else {
          this.showErrorMessage();
        }
      } else if (['failed', 'canceled'].includes(this.billPayment.status)) {
        this.showErrorMessage();
      } else if (this.billPayment.status === 'waiting') {
        this.pixWaiting = true;
        this.poolingPayment();
      } else {
        this.pixReady = true;
        this.hideAlert();
      }
    },
    copySuccess() {
      this.$tracker.logEvent('[pix] Copied to clipboard');
    },
    setLoadingTimeout() {
      setTimeout(() => {
        if (this.pixWaiting && !this.error) {
          this.$refs.alertBox.show(
            'warning', 'O pix está sendo processado',
            'Isso pode demorar alguns minutos, por favor volte mais tarde.',
          );
        }
      }, 10000);
    },
    showErrorMessage() {
      this.error = true;
      this.$refs.alertBox.show(
        'danger', 'Erro inesperado',
        'Não foi possível gerar o pix. Tente novamente mais tarde ou entre em contato com o Quero Pago.',
      );
    },
    resetPixStatus() {
      this.pixUnavailable = false;
      this.pixWaiting = false;
      this.pixReady = false;
    },
    hideAlert() {
      if (this.$refs.alertBox && !this.error) {
        this.$refs.alertBox.hide();
      }
    },
  },
  mounted() {
    const firstAttempt = true;
    this.loadBill().then(() => {
      if (this.bill.status === 'paid') {
        this.$router.push({ name: 'payment-receipt', params: { billId: this.bill.id } });
      } else {
        this.loadBillPayment(firstAttempt);
      }
    }).catch(this.showErrorMessage);
  },
};
</script>
